import React, { useState, useEffect } from 'react';
import {
    Table, Button, Tag, Space, message, Modal, Select, Card, Descriptions, Progress,
    Divider, Typography, Tooltip, Form, InputNumber, Switch, Row, Col, Input
} from 'antd';
import {
    EyeOutlined, CloseCircleOutlined, PauseCircleOutlined,
    PlayCircleOutlined, LinkOutlined, PlusOutlined, EditOutlined,
    StopOutlined, SyncOutlined, SettingOutlined, DollarOutlined
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../../shared/utils/api';
import subscriptionService from '../../services/subscriptionService';
import UserSelect from '../../components/UserSelect';
import AdminSubscriptionEditModal from '../../components/billing/AdminSubscriptionEditModal';
import SubscriptionStatusTag from '../../../shared/components/SubscriptionStatusTag';
import { formatMoney } from '../../../shared/utils/money';
import moment from 'moment';

const { Text } = Typography;
const { Option } = Select;

// 产品类型名（未列出的按代码大写显示）
const TYPE_LABEL = { disk: '云盘' };
const typeLabel = (code) => TYPE_LABEL[code] || code?.toUpperCase();

// 云盘配额/用量键 → 名称；字节 / GB 换算
const DISK_FEATURE_LABEL = {
    disk_traffic_bytes: '直链流量',
    disk_storage_gb: '存储空间',
    disk_mode: '类型',
    disk_seats: '成员数',
    disk_seats_5g: '5GB 席位',
    disk_seats_10g: '10GB 席位',
};
const DISK_MODE_LABEL = { personal: '个人', org_pool: '组织共享', org_seat: '组织按席位' };
const fmtBytes = (value) => {
    const v = Number(value) || 0;
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let n = v;
    let i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${i ? n.toFixed(2) : n} ${units[i]}`;
};
const featureLabel = (key) => DISK_FEATURE_LABEL[key] || key;
const diskFeatureValue = (key, v) => {
    if (key === 'disk_mode') return DISK_MODE_LABEL[v] || v;
    if (key === 'disk_storage_gb') return Number(v) >= 1024 ? `${+(Number(v) / 1024).toFixed(2)} TB` : `${Number(v) || 0} GB`;
    if (key === 'disk_traffic_bytes') return fmtBytes(v);
    return Number(v || 0).toLocaleString();
};
const giftTag = (r) => (r.gift
    ? <Tag color="magenta">{r.gift.source === 'product' ? '赠品 · 随邮箱套餐' : '赠品 · 随域名'} #{r.gift.parent_subscription_id}</Tag>
    : <Tag color="purple">附加包 #{r.parent_subscription_id}</Tag>);

const Subscriptions = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterProductType, setFilterProductType] = useState('');
    const [filterAutoRenew, setFilterAutoRenew] = useState('');
    const [filterUserId, setFilterUserId] = useState(undefined);
    const [searchParams] = useSearchParams();
    const [keyword, setKeyword] = useState(() => searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [createForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [renewalsModalVisible, setRenewalsModalVisible] = useState(false);
    const [pendingRenewals, setPendingRenewals] = useState([]);
    const [renewalsLoading, setRenewalsLoading] = useState(false);
    const [processingRenewals, setProcessingRenewals] = useState(false);
    const [renewalActionId, setRenewalActionId] = useState(null);
    const [quotaModalVisible, setQuotaModalVisible] = useState(false);
    const [quotaForm] = Form.useForm();
    const [quotaItems, setQuotaItems] = useState([]);

    useEffect(() => {
        loadProducts();
    }, []);

    useEffect(() => {
        loadSubscriptions(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus, filterProductType, filterUserId, keyword, filterAutoRenew]);

    // 服务端分页：page 缺省时刷新当前页
    const loadSubscriptions = async (page, pageSize) => {
        const current = typeof page === 'number' ? page : pagination.current;
        const size = pageSize || pagination.pageSize;
        setLoading(true);
        try {
            const params = { page: current, limit: size };
            if (filterStatus) params.status = filterStatus;
            if (filterProductType) params.product_type = filterProductType;
            if (filterAutoRenew !== '') params.auto_renew = filterAutoRenew;
            if (filterUserId) params.user_id = filterUserId;
            if (keyword) params.keyword = keyword;
            const response = await apiRequest('GET', '/api/admin/subscriptions/list', params);
            if (response.success) {
                setSubscriptions(Array.isArray(response.data) ? response.data : []);
                setPagination({
                    current: Number(response.page) || current,
                    pageSize: Number(response.limit) || size,
                    total: Number(response.total) || 0,
                });
            } else {
                message.error(response.error || '加载订阅列表失败');
            }
        } catch (error) {
            message.error('加载失败：' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await subscriptionService.getProducts();
            if (response.success) {
                setProducts(response.data || []);
            }
        } catch (error) {
            console.error('加载产品列表失败:', error);
        }
    };

    const handleViewDetail = async (record) => {
        try {
            const response = await subscriptionService.getSubscription(record.id);
            if (response.success) {
                setSelectedSubscription(response.data);
                setDetailModalVisible(true);
            } else {
                message.error(response.error || '加载订阅详情失败');
            }
        } catch (error) {
            message.error('加载失败：' + (error.error || error.message));
        }
    };

    // 深链 ?id= 直接打开详情（命令面板 / 用户详情「产品」）；?search= 预填搜索
    const deepId = searchParams.get('id');
    useEffect(() => {
        if (deepId) handleViewDetail({ id: deepId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepId]);

    const refreshDetail = async () => {
        if (!selectedSubscription) return;
        try {
            const response = await subscriptionService.getSubscription(selectedSubscription.id);
            if (response.success) {
                setSelectedSubscription(response.data);
            }
        } catch (error) {
            console.error('刷新详情失败:', error);
        }
    };

    const handleSuspend = (record) => {
        Modal.confirm({
            title: '确认暂停',
            content: `确定要暂停订阅 #${record.id} 吗？`,
            onOk: async () => {
                try {
                    const response = await apiRequest('POST', '/api/admin/subscriptions/suspend', { id: record.id });
                    if (response.success) {
                        message.success('暂停成功');
                        loadSubscriptions();
                    } else {
                        message.error(response.error || '暂停失败');
                    }
                } catch (error) {
                    message.error('暂停失败：' + error.message);
                }
            },
        });
    };

    const handleResume = (record) => {
        Modal.confirm({
            title: '确认恢复',
            content: `确定要恢复订阅 #${record.id} 吗？`,
            onOk: async () => {
                try {
                    const response = await apiRequest('POST', '/api/admin/subscriptions/resume', { id: record.id });
                    if (response.success) {
                        message.success('恢复成功');
                        loadSubscriptions();
                    } else {
                        message.error(response.error || '恢复失败');
                    }
                } catch (error) {
                    message.error('恢复失败：' + error.message);
                }
            },
        });
    };

    const handleCancel = (record) => {
        Modal.confirm({
            title: '确认取消',
            content: `确定要取消订阅 #${record.id} 吗？此操作不可逆。`,
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await apiRequest('POST', '/api/admin/subscriptions/cancel', { id: record.id });
                    if (response.success) {
                        message.success('取消成功');
                        loadSubscriptions();
                    } else {
                        message.error(response.error || '取消失败');
                    }
                } catch (error) {
                    message.error('取消失败：' + error.message);
                }
            },
        });
    };

    const handleTerminate = (record) => {
        Modal.confirm({
            title: '确认终止',
            content: `确定要立即终止订阅 #${record.id} 吗？终止后服务将立即停止，此操作不可逆。`,
            okType: 'danger',
            okText: '立即终止',
            onOk: async () => {
                try {
                    const response = await subscriptionService.terminateSubscription(record.id);
                    if (response.success) {
                        message.success('订阅已终止');
                        loadSubscriptions();
                    } else {
                        message.error(response.error || '终止失败');
                    }
                } catch (error) {
                    message.error('终止失败：' + (error.error || error.message));
                }
            },
        });
    };

    const handleToggleAutoRenew = async (record) => {
        setTogglingId(record.id);
        try {
            const response = await subscriptionService.toggleAutoRenew(record.id);
            if (response.success) {
                message.success(response.auto_renew ? '已开启自动续费' : '已关闭自动续费');
                setSubscriptions((prev) => prev.map((s) =>
                    s.id === record.id ? { ...s, auto_renew: response.auto_renew } : s
                ));
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败：' + (error.error || error.message));
        } finally {
            setTogglingId(null);
        }
    };

    // 续费管理
    const loadPendingRenewals = async () => {
        setRenewalsLoading(true);
        try {
            const response = await subscriptionService.getPendingRenewals(7);
            if (response.success) {
                setPendingRenewals(response.data || []);
            } else {
                message.error(response.error || '加载待续费列表失败');
            }
        } catch (error) {
            message.error('加载失败：' + (error.error || error.message));
        } finally {
            setRenewalsLoading(false);
        }
    };

    const handleOpenRenewals = () => {
        setRenewalsModalVisible(true);
        loadPendingRenewals();
    };

    const handleProcessRenewals = async () => {
        setProcessingRenewals(true);
        try {
            const response = await subscriptionService.processRenewals();
            if (response.success) {
                message.success('续费处理完成');
                loadPendingRenewals();
                loadSubscriptions();
            } else {
                message.error(response.error || '处理失败');
            }
        } catch (error) {
            message.error('处理失败：' + (error.error || error.message));
        } finally {
            setProcessingRenewals(false);
        }
    };

    const handleGenerateRenewal = async (record) => {
        setRenewalActionId(record.id);
        try {
            const response = await subscriptionService.generateRenewal(record.id);
            if (response.success) {
                message.success('续费发票已生成');
                loadPendingRenewals();
            } else {
                message.error(response.error || '生成失败');
            }
        } catch (error) {
            message.error('生成失败：' + (error.error || error.message));
        } finally {
            setRenewalActionId(null);
        }
    };

    // 自定义配额
    const handleOpenQuotaModal = () => {
        if (!selectedSubscription) return;
        const quotas = selectedSubscription.quotas || [];
        setQuotaItems(quotas);
        const initial = {};
        quotas.forEach((q) => {
            initial[q.feature_key] = q.is_unlimited ? -1 : Number(q.quota_limit ?? q.limit_value ?? 0);
        });
        quotaForm.setFieldsValue(initial);
        setQuotaModalVisible(true);
    };

    const handleUpdateQuota = async (values) => {
        setSubmitting(true);
        try {
            const response = await subscriptionService.updateQuota(selectedSubscription.id, values);
            if (response.success) {
                message.success('配额更新成功');
                setQuotaModalVisible(false);
                refreshDetail();
            } else {
                message.error(response.error || '更新失败');
            }
        } catch (error) {
            message.error('更新失败：' + (error.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 创建订阅
    const handleCreateSubscription = async (values) => {
        setSubmitting(true);
        try {
            const response = await subscriptionService.createSubscription(values);
            if (response.success) {
                message.success('订阅创建成功，发票已生成');
                setCreateModalVisible(false);
                createForm.resetFields();
                setSelectedProduct(null);
                loadSubscriptions();
            } else {
                message.error(response.error || '创建失败');
            }
        } catch (error) {
            message.error('创建失败：' + (error.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 编辑订阅
    const handleOpenEditModal = () => {
        if (selectedSubscription) setEditModalVisible(true);
    };

    const handleProductChange = (productId) => {
        const product = products.find(p => p.id === productId);
        setSelectedProduct(product);
        if (product && product.pricing && product.pricing.length > 0) {
            // 默认选择第一个可用的计费周期
            createForm.setFieldValue('billing_cycle', product.pricing[0].billing_cycle);
        }
    };

    const getStatusTag = (status) => <SubscriptionStatusTag status={status} />;

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '用户',
            dataIndex: 'username',
            key: 'username',
            width: 120,
        },
        {
            title: '产品名称',
            dataIndex: 'product_name',
            render: (v, r) => (r.parent_subscription_id ? <span>{v} {giftTag(r)}</span> : v),
            key: 'product_name',
            width: 150,
        },
        {
            title: '产品类型',
            dataIndex: 'product_type_code',
            key: 'product_type_code',
            width: 100,
            render: (code) => <Tag color="blue">{typeLabel(code)}</Tag>
        },
        {
            title: '关联资源',
            dataIndex: 'resource',
            key: 'resource',
            width: 180,
            render: (resource) => {
                if (!resource) {
                    return <Text type="secondary">-</Text>;
                }
                const statusColors = {
                    active: 'success',
                    inactive: 'default',
                    suspended: 'warning',
                    pending: 'processing',
                };
                return (
                    <Space size="small">
                        <Tooltip title={`状态: ${resource.status}`}>
                            <Tag color={statusColors[resource.status] || 'default'}>
                                {resource.type === 'disk_org' ? '云盘组织' : resource.type.toUpperCase()}
                            </Tag>
                        </Tooltip>
                        <Link to={resource.link}>
                            <Tooltip title="点击查看详情">
                                <span style={{ cursor: 'pointer' }}>
                                    {resource.name}
                                    <LinkOutlined style={{ marginLeft: 4 }} />
                                </span>
                            </Tooltip>
                        </Link>
                    </Space>
                );
            }
        },
        {
            title: '关联域名',
            dataIndex: 'linked_domain',
            key: 'linked_domain',
            width: 150,
            render: (linkedDomain) => {
                if (!linkedDomain) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Tooltip title={`域名注册赠送 | 到期: ${linkedDomain.expiry_date || '-'}`}>
                        <Link to={linkedDomain.link}>
                            <Tag color="green" icon={<LinkOutlined />}>
                                {linkedDomain.domain}
                            </Tag>
                        </Link>
                    </Tooltip>
                );
            }
        },
        {
            title: '计费周期',
            dataIndex: 'billing_cycle',
            key: 'billing_cycle',
            width: 100,
            render: (cycle) => {
                const cycleMap = {
                    onetime: '一次性',
                    monthly: '月付',
                    quarterly: '季付',
                    semi_annually: '半年付',
                    annually: '年付',
                };
                return cycleMap[cycle] || cycle;
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: '到期日期',
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            width: 110,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '自动续费',
            dataIndex: 'auto_renew',
            key: 'auto_renew',
            width: 90,
            render: (autoRenew, record) => (
                <Switch
                    size="small"
                    checked={!!autoRenew}
                    loading={togglingId === record.id}
                    checkedChildren="是"
                    unCheckedChildren="否"
                    onChange={() => handleToggleAutoRenew(record)}
                />
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 360,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        详情
                    </Button>
                    {record.status === 'active' && (
                        <Button
                            type="link"
                            size="small"
                            icon={<PauseCircleOutlined />}
                            onClick={() => handleSuspend(record)}
                        >
                            暂停
                        </Button>
                    )}
                    {record.status === 'suspended' && (
                        <Button
                            type="link"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            onClick={() => handleResume(record)}
                        >
                            恢复
                        </Button>
                    )}
                    {(record.status === 'active' || record.status === 'suspended') && !record.gift && (
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<CloseCircleOutlined />}
                            onClick={() => handleCancel(record)}
                        >
                            取消
                        </Button>
                    )}
                    {(record.status === 'active' || record.status === 'suspended' || record.status === 'pending') && (
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<StopOutlined />}
                            onClick={() => handleTerminate(record)}
                        >
                            终止
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    const billingCycleLabels = {
        onetime: '一次性',
        monthly: '月付',
        quarterly: '季付',
        semi_annually: '半年付',
        annually: '年付',
    };

    return (
        <div>
            <Card
                title="订阅管理"
                extra={
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalVisible(true)}
                        >
                            创建订阅
                        </Button>
                        <Button
                            icon={<SyncOutlined />}
                            onClick={handleOpenRenewals}
                        >
                            待续费
                        </Button>
                        <Button onClick={() => loadSubscriptions()} loading={loading}>
                            刷新
                        </Button>
                    </Space>
                }
            >
                <Space wrap style={{ marginBottom: 16 }}>
                    <Select
                        placeholder="状态筛选"
                        allowClear
                        style={{ width: 140 }}
                        onChange={(value) => setFilterStatus(value || '')}
                    >
                        <Option value="pending">待激活</Option>
                        <Option value="processing">开通中</Option>
                        <Option value="active">活跃</Option>
                        <Option value="suspended">暂停</Option>
                        <Option value="cancelled">已取消</Option>
                        <Option value="terminated">已终止</Option>
                        <Option value="expired">已过期</Option>
                    </Select>
                    <Select
                        placeholder="产品类型"
                        allowClear
                        style={{ width: 140 }}
                        onChange={(value) => setFilterProductType(value || '')}
                    >
                        <Option value="dns">DNS</Option>
                        <Option value="cdn">CDN</Option>
                        <Option value="ssl">SSL</Option>
                        <Option value="domain">域名</Option>
                        <Option value="host">独享容器</Option>
                        <Option value="host_shared">共享主机</Option>
                        <Option value="yeapmail">域名邮箱</Option>
                        <Option value="tm">事务邮件</Option>
                        <Option value="evm">云服务器</Option>
                        <Option value="lightapp">轻应用</Option>
                        <Option value="disk">云盘</Option>
                    </Select>
                    <Select
                        placeholder="自动续费"
                        allowClear
                        style={{ width: 120 }}
                        onChange={(value) => setFilterAutoRenew(value ?? '')}
                    >
                        <Option value="1">自动续费开</Option>
                        <Option value="0">自动续费关</Option>
                    </Select>
                    <UserSelect
                        placeholder="按用户筛选"
                        style={{ width: 220 }}
                        value={filterUserId}
                        onChange={(value) => setFilterUserId(value || undefined)}
                    />
                    <Input.Search
                        placeholder="订阅ID / 用户名 / 邮箱 / 产品名"
                        allowClear
                        style={{ width: 280 }}
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            if (!e.target.value) setKeyword('');
                        }}
                        onSearch={(value) => setKeyword((value || '').trim())}
                    />
                </Space>
                <Table
                    columns={columns}
                    dataSource={subscriptions}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1600 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100', '200'],
                        showTotal: (total) => `共 ${total} 条记录`,
                        onChange: (page, pageSize) => loadSubscriptions(page, pageSize),
                    }}
                />
            </Card>

            {/* 创建订阅模态框 */}
            <Modal
                title="创建订阅"
                open={createModalVisible}
                onCancel={() => {
                    setCreateModalVisible(false);
                    setSelectedProduct(null);
                    createForm.resetFields();
                }}
                onOk={() => createForm.submit()}
                confirmLoading={submitting}
                width={600}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreateSubscription}
                    initialValues={{ quantity: 1 }}
                >
                    <Form.Item
                        name="user_id"
                        label="用户"
                        rules={[{ required: true, message: '请选择用户' }]}
                    >
                        <UserSelect placeholder="选择用户" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="product_id"
                        label="产品"
                        rules={[{ required: true, message: '请选择产品' }]}
                    >
                        <Select
                            placeholder="选择产品"
                            onChange={handleProductChange}
                            showSearch
                            optionFilterProp="children"
                        >
                            {products.map(p => (
                                <Option key={p.id} value={p.id}>
                                    {p.name} ({p.product_type?.name || p.product_type_id})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="billing_cycle"
                                label="计费周期"
                                rules={[{ required: true, message: '请选择计费周期' }]}
                            >
                                <Select placeholder="选择计费周期">
                                    {selectedProduct?.pricing?.filter(p => p.enabled)?.map(pricing => (
                                        <Option key={pricing.billing_cycle} value={pricing.billing_cycle}>
                                            {billingCycleLabels[pricing.billing_cycle] || pricing.billing_cycle}
                                            {' - '}
                                            {formatMoney(pricing.price, pricing.currency)}
                                            {pricing.setup_fee > 0 && ` (+${formatMoney(pricing.setup_fee, pricing.currency)}开通费)`}
                                        </Option>
                                    )) || (
                                        <>
                                            <Option value="monthly">月付</Option>
                                            <Option value="quarterly">季付</Option>
                                            <Option value="semi_annually">半年付</Option>
                                            <Option value="annually">年付</Option>
                                        </>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="quantity" label="数量">
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="resource_id" label="资源 ID" rules={[{ required: true, message: '订阅必须挂在资源上' }]}
                        tooltip="CDN = 域名 ID，DNS = 区域 ID，邮箱 = 邮箱域名 ID。云服务器/主机等开通型产品请由用户下单开通">
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginTop: -8 }}>
                        <Text type="secondary">
                            生成待支付账单，付款后资源才切换到该订阅（金额为 0 的账单自动结清）。
                        </Text>
                    </div>
                </Form>
            </Modal>

            {/* 详情模态框 */}
            <Modal
                title="订阅详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="quota" icon={<SettingOutlined />} onClick={handleOpenQuotaModal}>
                        自定义配额
                    </Button>,
                    <Button key="edit" icon={<EditOutlined />} onClick={handleOpenEditModal}>
                        编辑
                    </Button>,
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        关闭
                    </Button>,
                ]}
                width={900}
            >
                {selectedSubscription && (
                    <>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="订阅ID">{selectedSubscription.id}</Descriptions.Item>
                            <Descriptions.Item label="状态">
                                {getStatusTag(selectedSubscription.status)}
                            </Descriptions.Item>
                            <Descriptions.Item label="用户ID">{selectedSubscription.user_id}</Descriptions.Item>
                            <Descriptions.Item label="用户名">{selectedSubscription.username || '-'}</Descriptions.Item>
                            <Descriptions.Item label="产品ID">{selectedSubscription.product_id}</Descriptions.Item>
                            <Descriptions.Item label="产品名称">{selectedSubscription.product_name}</Descriptions.Item>
                            <Descriptions.Item label="产品类型">
                                <Tag color="blue">{typeLabel(selectedSubscription.product_type_code)}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="计费周期">
                                {billingCycleLabels[selectedSubscription.billing_cycle] || selectedSubscription.billing_cycle}
                            </Descriptions.Item>
                            <Descriptions.Item label="激活日期">
                                {selectedSubscription.activation_date ? moment(selectedSubscription.activation_date).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="到期日期">
                                {selectedSubscription.expiry_date ? moment(selectedSubscription.expiry_date).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="自动续费">
                                {selectedSubscription.gift ? giftTag(selectedSubscription) : (
                                    <Tag color={selectedSubscription.auto_renew ? 'success' : 'default'}>
                                        {selectedSubscription.auto_renew ? '是' : '否'}
                                    </Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>

                        {selectedSubscription.plan_features && Object.keys(selectedSubscription.plan_features).length > 0 && (
                            <>
                                <Divider orientation="left">套餐规格</Divider>
                                <Descriptions bordered column={2} size="small">
                                    {Object.entries(selectedSubscription.plan_features).map(([k, v]) => (
                                        <Descriptions.Item key={k} label={featureLabel(k)}>{diskFeatureValue(k, v)}</Descriptions.Item>
                                    ))}
                                </Descriptions>
                            </>
                        )}

                        {/* 关联域名信息 */}
                        {selectedSubscription.linked_domain && (
                            <>
                                <Divider orientation="left">关联域名（域名注册赠送）</Divider>
                                <Descriptions bordered column={2} size="small">
                                    <Descriptions.Item label="域名">{selectedSubscription.linked_domain.domain}</Descriptions.Item>
                                    <Descriptions.Item label="状态">
                                        <Tag color={selectedSubscription.linked_domain.status === 'active' ? 'success' : 'default'}>
                                            {selectedSubscription.linked_domain.status}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="域名到期日期">
                                        {selectedSubscription.linked_domain.expiry_date || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="操作">
                                        <Link to={selectedSubscription.linked_domain.link}>
                                            <Button type="link" size="small" icon={<LinkOutlined />}>
                                                查看域名详情
                                            </Button>
                                        </Link>
                                    </Descriptions.Item>
                                </Descriptions>
                                <div style={{ background: '#fffbe6', padding: 12, borderRadius: 4, marginTop: 8 }}>
                                    <Text type="warning">
                                        此DNS服务由域名注册赠送，将随域名一起续费或过期。如需独立管理，请升级为付费DNS套餐。
                                    </Text>
                                </div>
                            </>
                        )}

                        {/* 附加资源包（子订阅） */}
                        {!selectedSubscription.parent_subscription_id && (
                            <>
                                <Divider orientation="left">附加资源包</Divider>
                                <Space wrap style={{ marginBottom: 8 }}>
                                    {(selectedSubscription.addons || []).map((a) => (
                                        <Tag key={a.subscription_id} color={a.status === 'active' ? 'blue' : 'orange'}>
                                            {a.name}{a.quantity > 1 ? ` × ${a.quantity}` : ''}（#{a.subscription_id} {a.status}）
                                            {a.status !== 'terminated' && (
                                                <Button type="link" size="small" danger style={{ padding: '0 0 0 4px', height: 'auto' }}
                                                    onClick={() => {
                                                        Modal.confirm({
                                                            title: '取消该附加包？立即失效，不自动退款。',
                                                            onOk: async () => {
                                                                const r = await subscriptionService.cancelAddon(a.subscription_id);
                                                                if (r.success) { message.success('已取消'); refreshDetail(); }
                                                                else message.error(r.error || '失败');
                                                            },
                                                        });
                                                    }}>取消</Button>
                                            )}
                                        </Tag>
                                    ))}
                                    {(selectedSubscription.addons || []).length === 0 && <Text type="secondary">无</Text>}
                                </Space>
                                {(selectedSubscription.addon_catalog || []).length > 0 && (
                                    <Space wrap>
                                        {selectedSubscription.addon_catalog.map((a) => (
                                            <Button key={a.product_id} size="small" onClick={() => {
                                                Modal.confirm({
                                                    title: `赠送「${a.name}」给该用户？`,
                                                    content: '管理员赠送：不出账单、立即生效；周期包随主套餐到期。',
                                                    onOk: async () => {
                                                        const r = await subscriptionService.grantAddon(selectedSubscription.id, a.product_id, 1);
                                                        if (r.success) { message.success('已赠送'); refreshDetail(); }
                                                        else message.error(r.error || '失败');
                                                    },
                                                });
                                            }}>+ 赠送 {a.name}</Button>
                                        ))}
                                    </Space>
                                )}
                            </>
                        )}

                        {/* 使用量显示 */}
                        {selectedSubscription.usage && selectedSubscription.usage.length > 0 && (
                            <>
                                <Divider orientation="left">
                                    周期性使用量
                                    <Button type="link" size="small" onClick={async () => {
                                        const r = await subscriptionService.recomputeUsage(selectedSubscription.id);
                                        if (r.success) { message.success('已按产品+附加包重算上限'); refreshDetail(); }
                                        else message.error(r.error || '失败');
                                    }}>重算上限</Button>
                                </Divider>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                                    {selectedSubscription.usage.map((item, index) => {
                                        const percentage = item.is_unlimited ? 0 :
                                            (item.limit_value > 0 ? Math.min(100, (item.used_value / item.limit_value) * 100) : 0);
                                        const isExceeded = !item.is_unlimited && item.used_value > item.limit_value;

                                        const formatValue = (value, key) => {
                                            if (key === 'disk_traffic_bytes') return fmtBytes(value);
                                            if (key === 'traffic_bytes' || key === 'bandwidth_bytes') {
                                                const tb = value / (1024 * 1024 * 1024 * 1024);
                                                if (tb >= 1) return `${tb.toFixed(2)} TB`;
                                                const gb = value / (1024 * 1024 * 1024);
                                                if (gb >= 1) return `${gb.toFixed(2)} GB`;
                                                const mb = value / (1024 * 1024);
                                                if (mb >= 1) return `${mb.toFixed(2)} MB`;
                                                const kb = value / 1024;
                                                if (kb >= 1) return `${kb.toFixed(2)} KB`;
                                                return `${value} B`;
                                            }
                                            if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
                                            if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
                                            return value.toString();
                                        };

                                        const featureLabels = {
                                            'traffic_bytes': '流量',
                                            'requests': '请求数',
                                            'dns_queries': 'DNS查询数',
                                            'bandwidth_bytes': '流量',
                                            ...DISK_FEATURE_LABEL,
                                        };

                                        const resetCycleLabels = {
                                            'daily': '每日',
                                            'weekly': '每周',
                                            'monthly': '每月',
                                            'quarterly': '每季度',
                                            'yearly': '每年',
                                        };

                                        return (
                                            <Card
                                                key={index}
                                                size="small"
                                                title={
                                                    <span>
                                                        {featureLabels[item.feature_key] || item.feature_key}
                                                        <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
                                                            {resetCycleLabels[item.reset_cycle] || item.reset_cycle || '每月'}重置
                                                        </Tag>
                                                    </span>
                                                }
                                                extra={isExceeded ? <Tag color="error">已超限</Tag> : null}
                                            >
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text strong>
                                                        {formatValue(item.used_value, item.feature_key)}
                                                    </Text>
                                                    <Text type="secondary">
                                                        {' / '}
                                                        {item.is_unlimited ? '无限制' : formatValue(item.limit_value, item.feature_key)}
                                                    </Text>
                                                </div>
                                                {!item.is_unlimited && (
                                                    <Progress
                                                        percent={parseFloat(percentage.toFixed(1))}
                                                        status={isExceeded ? 'exception' : (percentage >= 90 ? 'exception' : percentage >= 70 ? 'active' : 'normal')}
                                                        size="small"
                                                    />
                                                )}
                                                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                                    <div>周期: {moment(item.period_start).format('MM-DD HH:mm')} ~ {moment(item.period_end).format('MM-DD HH:mm')}</div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}
            </Modal>

            {/* 编辑订阅：套餐 / 周期 / 到期 / 自动续费（与用户详情、CDN 域名共用） */}
            <AdminSubscriptionEditModal
                open={editModalVisible}
                subscriptionId={selectedSubscription?.id}
                onClose={() => setEditModalVisible(false)}
                onSaved={() => { refreshDetail(); loadSubscriptions(); }}
            />

            {/* 待续费管理模态框 */}
            <Modal
                title="待续费订阅（7天内到期）"
                open={renewalsModalVisible}
                onCancel={() => setRenewalsModalVisible(false)}
                footer={[
                    <Button
                        key="process"
                        type="primary"
                        icon={<DollarOutlined />}
                        loading={processingRenewals}
                        onClick={handleProcessRenewals}
                    >
                        批量处理续费
                    </Button>,
                    <Button key="refresh" onClick={loadPendingRenewals} loading={renewalsLoading}>
                        刷新
                    </Button>,
                    <Button key="close" onClick={() => setRenewalsModalVisible(false)}>
                        关闭
                    </Button>,
                ]}
                width={900}
            >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    「批量处理续费」会为所有到期订阅生成续费发票并尝试自动扣款（仅对开启自动续费的订阅）；也可对单条订阅手动生成续费发票。
                </Text>
                <Table
                    dataSource={pendingRenewals}
                    rowKey="id"
                    loading={renewalsLoading}
                    size="small"
                    pagination={false}
                    scroll={{ x: 720 }}
                    columns={[
                        { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
                        { title: '用户', dataIndex: 'username', key: 'username', width: 120,
                          render: (v, r) => v || r.user_id },
                        { title: '产品', dataIndex: 'product_name', key: 'product_name', width: 150 },
                        { title: '计费周期', dataIndex: 'billing_cycle', key: 'billing_cycle', width: 100,
                          render: (c) => billingCycleLabels[c] || c },
                        { title: '到期日期', dataIndex: 'expiry_date', key: 'expiry_date', width: 110,
                          render: (d) => d ? moment(d).format('YYYY-MM-DD') : '-' },
                        { title: '自动续费', dataIndex: 'auto_renew', key: 'auto_renew', width: 80,
                          render: (a) => <Tag color={a ? 'success' : 'default'}>{a ? '是' : '否'}</Tag> },
                        {
                            title: '操作',
                            key: 'action',
                            width: 130,
                            render: (_, record) => (
                                <Button
                                    type="link"
                                    size="small"
                                    loading={renewalActionId === record.id}
                                    onClick={() => handleGenerateRenewal(record)}
                                >
                                    生成续费发票
                                </Button>
                            ),
                        },
                    ]}
                />
            </Modal>

            {/* 自定义配额模态框 */}
            <Modal
                title="自定义配额"
                open={quotaModalVisible}
                onCancel={() => setQuotaModalVisible(false)}
                onOk={() => quotaForm.submit()}
                confirmLoading={submitting}
                width={520}
            >
                {quotaItems.length === 0 ? (
                    <Text type="secondary">该订阅暂无可配置的配额项。</Text>
                ) : (
                    <Form form={quotaForm} layout="vertical" onFinish={handleUpdateQuota}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            填写 -1 表示无限制。修改后立即生效并覆盖套餐默认配额。
                        </Text>
                        {quotaItems.map((q) => (
                            <Form.Item
                                key={q.feature_key}
                                name={q.feature_key}
                                label={featureLabel(q.feature_key)}
                            >
                                <InputNumber min={-1} style={{ width: '100%' }} />
                            </Form.Item>
                        ))}
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default Subscriptions;
